import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { useState } from "react";
import styled from "styled-components";
import AddNewPermissionModal from "./modals/AddNewPermissionModal";
import EnableStorePermissionModal from "./modals/EnableStorePermissionModal";
import EnableCountryPermissionModal from "./modals/EnableCountryPermissionModal";
import EnableUserPermissionModal from "./modals/EnableUserPermissionModal";
import DeleteConfirmationModal from "./modals/DeleteConfirmationModal";
import PermissionsPanel from "./panels/PermissionsPanel";
import CountryPermissionsPanel from "./panels/CountryPermissionsPanel";
import StorePermissionsPanel from "./panels/StorePermissionsPanel";
import UserPermissionsPanel from "./panels/UserPermissionsPanel";
import {
  useGetAllPermissions,
  useGetAllCountryPermissions,
  useGetAllStorePermissions,
  useUpdateCountryPermission,
  useDeleteCountryPermission,
  useUpdateStorePermission,
  useDeleteStorePermission,
  useRefreshUserScopedPermissions,
  useGetAllUserPermissions,
  useUpdateUserPermission,
  useDeleteUserPermission,
} from "../repositories/permissions.repository";
import FixaTabs, {
  FixaTab,
  FixaTabPanel,
} from "../../shared/wrappers/FixaTabs/FixaTabs";
import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import RoleType from "@coworker/enums/roleType";

const ButtonSection = styled.div`
  display: flex;
  gap: 12px;
`;

const Container = styled.div`
  padding: 16px;
`;

const FeatureFlagOverviewPage = () => {
  const [createPermissionModalOpen, setCreatePermissionModalOpen] =
    useState(false);
  const [
    createCountryPermissionModalOpen,
    setCreateCountryPermissionModalOpen,
  ] = useState(false);
  const [createStorePermissionModalOpen, setCreateStorePermissionModalOpen] =
    useState(false);
  const [createUserPermissionModalOpen, setCreateUserPermissionModalOpen] =
    useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState<number | null>(
    null
  );
  const [deleteType, setDeleteType] = useState<"country" | "store" | "user">(
    "country"
  );

  const { data: permissions = [], isLoading: isLoadingPermissions } =
    useGetAllPermissions();

  const {
    data: countryPermissions = [],
    isLoading: isLoadingCountryPermissions,
  } = useGetAllCountryPermissions();

  // Store permissions hook
  const { data: storePermissions = [], isLoading: isLoadingStorePermissions } =
    useGetAllStorePermissions();

  // User permissions hook
  const { data: userPermissions = [], isLoading: isLoadingUserPermissions } =
    useGetAllUserPermissions();

  const updateCountryPermissionMutation = useUpdateCountryPermission();
  const deleteCountryPermissionMutation = useDeleteCountryPermission();
  const updateStorePermissionMutation = useUpdateStorePermission();
  const deleteStorePermissionMutation = useDeleteStorePermission();
  const updateUserPermissionMutation = useUpdateUserPermission();
  const deleteUserPermissionMutation = useDeleteUserPermission();
  const refreshUserScopedPermissionsMutation =
    useRefreshUserScopedPermissions();

  const handleCountryPermissionToggle = async (
    permissionId: number,
    currentValue: boolean
  ) => {
    try {
      await updateCountryPermissionMutation.mutateAsync({
        id: permissionId,
        data: { value: !currentValue },
      });
    } catch (error) {
      console.error("Failed to update country permission:", error);
    }
  };

  const handleDeleteClick = (permissionId: number) => {
    setPermissionToDelete(permissionId);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!permissionToDelete) return;

    try {
      if (deleteType === "country") {
        await deleteCountryPermissionMutation.mutateAsync(permissionToDelete);
      } else if (deleteType === "store") {
        await deleteStorePermissionMutation.mutateAsync(permissionToDelete);
      } else if (deleteType === "user") {
        await deleteUserPermissionMutation.mutateAsync(permissionToDelete);
      }
      setDeleteConfirmationOpen(false);
      setPermissionToDelete(null);
      setDeleteType("country");
    } catch (error) {
      console.error("Failed to delete permission:", error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmationOpen(false);
    setPermissionToDelete(null);
    setDeleteType("country");
  };

  const handleStorePermissionToggle = async (
    permissionId: number,
    currentValue: boolean
  ) => {
    await updateStorePermissionMutation.mutateAsync({
      id: permissionId,
      data: { value: !currentValue },
    });
  };

  const handleDeleteStorePermissionClick = (permissionId: number) => {
    setPermissionToDelete(permissionId);
    setDeleteType("store");
    setDeleteConfirmationOpen(true);
  };

  const handleUserPermissionToggle = async (
    permissionId: number,
    currentValue: boolean
  ) => {
    await updateUserPermissionMutation.mutateAsync({
      id: permissionId,
      data: { value: !currentValue },
    });
  };

  const handleDeleteUserPermissionClick = (permissionId: number) => {
    setPermissionToDelete(permissionId);
    setDeleteType("user");
    setDeleteConfirmationOpen(true);
  };

  const handleRunUpdateJob = async () => {
    try {
      await refreshUserScopedPermissionsMutation.mutateAsync();
    } catch (error) {
      console.error("Failed to refresh user scoped permissions:", error);
    }
  };
  const { getRoleId } = useLoggedInUser();

  if (![RoleType.SUPERUSER].includes(getRoleId())) return null;

  return (
    <Container>
      <AddNewPermissionModal
        isVisible={createPermissionModalOpen}
        setIsOpen={setCreatePermissionModalOpen}
      />
      <EnableCountryPermissionModal
        isVisible={createCountryPermissionModalOpen}
        setIsOpen={setCreateCountryPermissionModalOpen}
      />
      <EnableStorePermissionModal
        isVisible={createStorePermissionModalOpen}
        setIsOpen={setCreateStorePermissionModalOpen}
      />
      <EnableUserPermissionModal
        isVisible={createUserPermissionModalOpen}
        setIsOpen={setCreateUserPermissionModalOpen}
      />
      <DeleteConfirmationModal
        visible={deleteConfirmationOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <ButtonSection>
        <FixaButton
          onClick={() => {
            setCreatePermissionModalOpen(true);
          }}
          text={"Add new permission"}
          size="medium"
          type="primary"
        />

        <FixaButton
          onClick={() => {
            setCreateCountryPermissionModalOpen(true);
          }}
          text={"Add new country permission"}
          size="medium"
          type="primary"
        />

        <FixaButton
          onClick={() => {
            setCreateStorePermissionModalOpen(true);
          }}
          text={"Add new store permission"}
          size="medium"
          type="primary"
        />
        <FixaButton
          onClick={() => {
            setCreateUserPermissionModalOpen(true);
          }}
          text={"Add new user permission"}
          size="medium"
          type="primary"
        />
        <FixaButton
          onClick={handleRunUpdateJob}
          text={"Run update job"}
          size="medium"
          type="primary"
        />
      </ButtonSection>
      <FixaTabs
        defaultActiveTab="0"
        tabs={[
          <FixaTab key="0" tabPanelId="0" text={"Permissions"} />,
          <FixaTab key="1" tabPanelId="1" text={"Country permissions"} />,
          <FixaTab key="2" tabPanelId="2" text={"Store permissions"} />,
          <FixaTab key="3" tabPanelId="3" text={"User permissions"} />,
        ]}
        tabPanels={[
          <FixaTabPanel key="permissions" tabPanelId={"0"}>
            <PermissionsPanel
              permissions={permissions}
              isLoading={isLoadingPermissions}
            />
          </FixaTabPanel>,
          <FixaTabPanel key="country-permissions" tabPanelId={"1"}>
            <CountryPermissionsPanel
              permissions={countryPermissions}
              isLoading={isLoadingCountryPermissions}
              onToggle={handleCountryPermissionToggle}
              onDelete={handleDeleteClick}
            />
          </FixaTabPanel>,
          <FixaTabPanel key="store-permissions" tabPanelId={"2"}>
            <StorePermissionsPanel
              permissions={storePermissions}
              isLoading={isLoadingStorePermissions}
              onToggle={handleStorePermissionToggle}
              onDelete={handleDeleteStorePermissionClick}
            />
          </FixaTabPanel>,
          <FixaTabPanel key="user-permissions" tabPanelId={"3"}>
            <UserPermissionsPanel
              permissions={userPermissions}
              isLoading={isLoadingUserPermissions}
              onToggle={handleUserPermissionToggle}
              onDelete={handleDeleteUserPermissionClick}
            />
          </FixaTabPanel>,
        ]}
      ></FixaTabs>
    </Container>
  );
};

export default FeatureFlagOverviewPage;
